// ─────────────────────────────────────────────
//  src/pages/Watchlist.js  —  Phase 5
//  Full watchlist tracker:
//  • Live CMP + day change for each stock
//  • Target entry price + distance indicator
//  • Priority (High / Medium / Low)
//  • Notes per stock
//  • Sort by any column
//  • Add / edit / remove
//  • One-click "Move to Portfolio"
//  • Search / filter
// ─────────────────────────────────────────────
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useWatchlist } from "../hooks/useWatchlist";
import { usePortfolio } from "../hooks/usePortfolio";
import "./Watchlist.css";

const fmtINR = (n) =>
  n != null && n !== ""
    ? "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })
    : "—";

const fmtPct = (n) => (n >= 0 ? "+" : "") + Number(n).toFixed(2) + "%";

// ── Add / Edit modal ──────────────────────────
function WatchlistModal({ initial = null, onSave, onClose }) {
  const [form, setForm] = useState({
    symbol:             initial?.symbol             || "",
    exchange:           initial?.exchange           || "NSE",
    target_entry_price: initial?.target_entry_price || "",
    stop_loss:          initial?.stop_loss          || "",
    priority:           initial?.priority           || "medium",
    notes:              initial?.notes              || "",
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");
  const isEdit = !!initial;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    const sym = form.symbol.trim().toUpperCase();
    if (!sym) return setErr("Symbol is required.");
    setSaving(true);
    try {
      await onSave({ ...form, symbol: sym });
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="wl-modal-overlay" onClick={onClose}>
      <div className="wl-modal" onClick={e => e.stopPropagation()}>
        <div className="wl-modal-header">
          <h3>{isEdit ? `Edit — ${initial.symbol}` : "Add to Watchlist"}</h3>
          <button className="wl-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="wl-modal-body">
          {/* Symbol + Exchange */}
          <div className="wl-field-row">
            <div className="wl-field">
              <label>Symbol *</label>
              <input
                placeholder="e.g. RELIANCE"
                value={form.symbol}
                disabled={isEdit}
                onChange={e => set("symbol", e.target.value.toUpperCase())}
              />
            </div>
            <div className="wl-field">
              <label>Exchange</label>
              <select value={form.exchange} onChange={e => set("exchange", e.target.value)}>
                <option value="NSE">NSE</option>
                <option value="BSE">BSE</option>
              </select>
            </div>
          </div>

          {/* Target + Stop Loss */}
          <div className="wl-field-row">
            <div className="wl-field">
              <label>Target Entry Price (₹)</label>
              <input
                type="number" min="0" step="0.01" placeholder="e.g. 2400"
                value={form.target_entry_price}
                onChange={e => set("target_entry_price", e.target.value)}
              />
            </div>
            <div className="wl-field">
              <label>Stop Loss (₹)</label>
              <input
                type="number" min="0" step="0.01" placeholder="e.g. 2100"
                value={form.stop_loss}
                onChange={e => set("stop_loss", e.target.value)}
              />
            </div>
          </div>

          {/* Priority */}
          <div className="wl-field">
            <label>Priority</label>
            <select value={form.priority} onChange={e => set("priority", e.target.value)}>
              <option value="high">🔴 High — Strong conviction</option>
              <option value="medium">🟡 Medium — Watching closely</option>
              <option value="low">⚪ Low — On radar</option>
            </select>
          </div>

          {/* Notes */}
          <div className="wl-field">
            <label>Notes / Thesis</label>
            <textarea
              rows={3}
              placeholder="Why are you watching this? Catalyst, setup, thesis…"
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
            />
          </div>

          {err && <div style={{ color: "#ef4444", fontSize: "0.8rem" }}>⚠ {err}</div>}
        </div>
        <div className="wl-modal-footer">
          <button className="wl-btn wl-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="wl-btn wl-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add to Watchlist"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Move to Portfolio modal ───────────────────
function MoveToPortfolioModal({ item, onSave, onClose }) {
  const [qty,   setQty]   = useState("");
  const [price, setPrice] = useState(item.target_entry_price || "");
  const [date,  setDate]  = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  async function handleMove() {
    if (!qty || !price) return;
    setSaving(true);
    try {
      await onSave({
        symbol:    item.symbol,
        exchange:  item.exchange || "NSE",
        type:      "BUY",
        qty:       parseFloat(qty),
        price:     parseFloat(price),
        date,
        notes:     `Moved from watchlist`,
        source:    "watchlist",
      });
      onClose();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  }

  return (
    <div className="wl-modal-overlay" onClick={onClose}>
      <div className="wl-modal" onClick={e => e.stopPropagation()}>
        <div className="wl-modal-header">
          <h3>Move {item.symbol} → Portfolio</h3>
          <button className="wl-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="wl-modal-body">
          <div style={{ padding: "8px 12px", background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.15)", borderRadius: 8, fontSize: "0.8rem", color: "#94a3b8" }}>
            This will add a BUY transaction to your portfolio and remove {item.symbol} from your watchlist.
          </div>
          <div className="wl-field-row">
            <div className="wl-field">
              <label>Quantity *</label>
              <input type="number" min="1" placeholder="10" value={qty} onChange={e => setQty(e.target.value)} />
            </div>
            <div className="wl-field">
              <label>Buy Price (₹) *</label>
              <input type="number" min="0" step="0.01" placeholder={price || "0"} value={price} onChange={e => setPrice(e.target.value)} />
            </div>
          </div>
          <div className="wl-field">
            <label>Trade Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          {qty && price && (
            <div style={{ fontSize: "0.82rem", color: "#64748b" }}>
              Total: <span style={{ color: "#e2e8f0", fontFamily: "DM Mono", fontWeight: 700 }}>
                ₹{(parseFloat(qty) * parseFloat(price)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
        <div className="wl-modal-footer">
          <button className="wl-btn wl-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="wl-btn wl-btn-green"
            onClick={handleMove}
            disabled={saving || !qty || !price}
          >
            {saving ? "Moving…" : "Confirm Buy & Move"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Watchlist Page ───────────────────────
export default function Watchlist() {
  const navigate = useNavigate();
  const { items, liveData, loading, addToWatchlist, removeFromWatchlist, updateWatchlistItem } = useWatchlist();
  const { addTransaction } = usePortfolio();

  const [showAddModal,  setShowAddModal]  = useState(false);
  const [editItem,      setEditItem]      = useState(null);
  const [moveItem,      setMoveItem]      = useState(null);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [sortBy,        setSortBy]        = useState("added_date");
  const [sortDir,       setSortDir]       = useState("desc");

  // ── Enrich items with live data ─────────────
  const enriched = items.map(item => {
    const live      = liveData[item.symbol] || {};
    const cmp       = parseFloat(live.price) || 0;
    const chgPct    = parseFloat(live.change_percent) || 0;
    const chg       = parseFloat(live.change)         || 0;
    const target    = parseFloat(item.target_entry_price) || 0;
    const distPct   = cmp && target ? ((cmp - target) / target) * 100 : null;
    return { ...item, cmp, chgPct, chg, distPct, liveName: live.name || item.symbol };
  });

  // ── Summary stats ───────────────────────────
  const withCmp   = enriched.filter(i => i.cmp > 0);
  const atTarget  = enriched.filter(i => i.distPct !== null && i.distPct <= 0).length;
  const highCount = enriched.filter(i => i.priority === "high").length;

  // ── Filter + sort ───────────────────────────
  const filtered = useMemo(() => {
    let arr = enriched;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      arr = arr.filter(i => i.symbol.toLowerCase().includes(q) || (i.notes || "").toLowerCase().includes(q));
    }
    if (priorityFilter !== "all") arr = arr.filter(i => (i.priority || "medium") === priorityFilter);
    arr = [...arr].sort((a, b) => {
      const mult = sortDir === "asc" ? 1 : -1;
      const va = a[sortBy] ?? 0;
      const vb = b[sortBy] ?? 0;
      return typeof va === "string" ? va.localeCompare(vb) * mult : (va - vb) * mult;
    });
    return arr;
  }, [enriched, searchQuery, priorityFilter, sortBy, sortDir]);

  function handleSort(field) {
    if (sortBy === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("desc"); }
  }
  const thCls = (f) => sortBy === f ? `sort-${sortDir}` : "";

  // ── Move to portfolio handler ────────────────
  async function handleMoveToPortfolio(txData) {
    await addTransaction(txData);
    await removeFromWatchlist(moveItem.id);
  }

  // ── Distance pill ───────────────────────────
  function DistPill({ distPct, target }) {
    if (!target) return <span className="wl-dist-pill none">No target</span>;
    if (distPct === null) return <span className="wl-dist-pill none">—</span>;
    if (distPct <= 0) return <span className="wl-dist-pill hit">✓ At/below entry</span>;
    if (distPct <= 5) return <span className="wl-dist-pill above">~{distPct.toFixed(1)}% away</span>;
    return <span className="wl-dist-pill below">{distPct.toFixed(1)}% to entry</span>;
  }

  if (loading) {
    return (
      <div className="wl-page">
        {[1,2,3,4,5].map(i => <div key={i} className="wl-skel-row" />)}
      </div>
    );
  }

  return (
    <div className="wl-page">

      {/* ── Header ── */}
      <div className="wl-header">
        <div className="wl-header-left">
          <h1>👁 My Watchlist</h1>
          <p>{items.length} stocks · watching before pulling the trigger</p>
        </div>
        <div className="wl-header-actions">
          <button className="wl-btn wl-btn-primary" onClick={() => setShowAddModal(true)}>
            ＋ Add Stock
          </button>
        </div>
      </div>

      {/* ── Summary bar ── */}
      {items.length > 0 && (
        <div className="wl-summary-bar">
          <div className="wl-stat">
            <span className="wl-stat-label">Watching</span>
            <span className="wl-stat-val">{items.length}</span>
          </div>
          <div className="wl-stat">
            <span className="wl-stat-label">High Priority</span>
            <span className="wl-stat-val" style={{ color: highCount > 0 ? "#ef4444" : "#e2e8f0" }}>{highCount}</span>
          </div>
          <div className="wl-stat">
            <span className="wl-stat-label">At/Near Target</span>
            <span className={`wl-stat-val ${atTarget > 0 ? "up" : ""}`}>{atTarget}</span>
          </div>
          <div className="wl-stat">
            <span className="wl-stat-label">Avg Day Change</span>
            <span className={`wl-stat-val ${withCmp.length ? (withCmp.reduce((s,i)=>s+i.chgPct,0)/withCmp.length >= 0 ? "up" : "down") : ""}`}>
              {withCmp.length
                ? fmtPct(withCmp.reduce((s,i) => s + i.chgPct, 0) / withCmp.length)
                : "—"}
            </span>
          </div>
        </div>
      )}

      {/* ── Main Panel ── */}
      <div className="wl-panel">
        {/* Panel header + filters */}
        <div className="wl-panel-header">
          <span className="wl-panel-title">Watchlist ({filtered.length})</span>
          <div className="wl-filter-row">
            {/* Search */}
            <div className="wl-search-wrap">
              <span className="wl-search-icon">🔍</span>
              <input
                placeholder="Search symbol or notes…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            {/* Priority filter */}
            {["all","high","medium","low"].map(p => (
              <button
                key={p}
                onClick={() => setPriorityFilter(p)}
                style={{
                  padding: "5px 12px", fontSize: "0.72rem", fontWeight: 600,
                  borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
                  background: priorityFilter === p ? "rgba(59,130,246,0.15)" : "transparent",
                  color: priorityFilter === p ? "#60a5fa" : "#475569",
                  border: priorityFilter === p ? "1px solid rgba(59,130,246,0.3)" : "1px solid #1e2d45",
                  transition: "all 0.15s",
                }}
              >
                {p === "all" ? "All" : p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Table or Empty */}
        {!items.length ? (
          <div className="wl-empty">
            <div className="wl-empty-icon">👁</div>
            <p className="wl-empty-title">Nothing on your radar yet</p>
            <p className="wl-empty-sub">Add stocks you're watching with a target entry price and thesis. When the price is right, move them to your portfolio in one click.</p>
            <button className="wl-btn wl-btn-primary" style={{ marginTop: 8 }} onClick={() => setShowAddModal(true)}>
              ＋ Add Your First Stock
            </button>
          </div>
        ) : !filtered.length ? (
          <div className="wl-empty">
            <div className="wl-empty-icon">🔍</div>
            <p className="wl-empty-title">No matches</p>
            <p className="wl-empty-sub">Try a different search or filter.</p>
          </div>
        ) : (
          <div className="wl-table-wrap">
            <table className="wl-table">
              <thead>
                <tr>
                  <th className={thCls("symbol")}   onClick={() => handleSort("symbol")}>Stock</th>
                  <th className={thCls("cmp")}       onClick={() => handleSort("cmp")}>CMP</th>
                  <th className={thCls("chgPct")}    onClick={() => handleSort("chgPct")}>Day</th>
                  <th className={thCls("target_entry_price")} onClick={() => handleSort("target_entry_price")}>Target Entry</th>
                  <th className={thCls("distPct")}   onClick={() => handleSort("distPct")}>Distance</th>
                  <th className={thCls("stop_loss")} onClick={() => handleSort("stop_loss")}>Stop Loss</th>
                  <th className={thCls("priority")}  onClick={() => handleSort("priority")}>Priority</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id} onClick={() => navigate(`/stock/${item.symbol}`)}>

                    {/* Symbol */}
                    <td>
                      <div className="wl-sym-cell">
                        <span className="wl-sym">{item.symbol}</span>
                        <span className="wl-name">{item.liveName !== item.symbol ? item.liveName : ""}</span>
                      </div>
                    </td>

                    {/* CMP */}
                    <td className="wl-mono">
                      {item.cmp > 0 ? fmtINR(item.cmp) : <span className="wl-muted">—</span>}
                    </td>

                    {/* Day change */}
                    <td className={`wl-mono ${item.chgPct > 0 ? "wl-up" : item.chgPct < 0 ? "wl-down" : "wl-muted"}`}>
                      {item.cmp > 0 ? fmtPct(item.chgPct) : "—"}
                    </td>

                    {/* Target entry */}
                    <td className="wl-mono">
                      {item.target_entry_price ? fmtINR(item.target_entry_price) : <span className="wl-muted">—</span>}
                    </td>

                    {/* Distance to target */}
                    <td onClick={e => e.stopPropagation()}>
                      <DistPill distPct={item.distPct} target={item.target_entry_price} />
                    </td>

                    {/* Stop Loss */}
                    <td className="wl-mono">
                      {item.stop_loss ? fmtINR(item.stop_loss) : <span className="wl-muted">—</span>}
                    </td>

                    {/* Priority */}
                    <td onClick={e => e.stopPropagation()}>
                      <span className={`wl-priority ${item.priority || "medium"}`}>
                        {item.priority || "medium"}
                      </span>
                    </td>

                    {/* Notes */}
                    <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", color: "#475569", fontSize: "0.75rem" }}>
                      {item.notes || <span className="wl-muted">—</span>}
                    </td>

                    {/* Actions */}
                    <td onClick={e => e.stopPropagation()}>
                      <div className="wl-row-actions">
                        <button className="wl-row-btn analyze" title="Analyse" onClick={() => navigate(`/stock/${item.symbol}`)}>📈</button>
                        <button className="wl-row-btn move"    title="Move to Portfolio" onClick={() => setMoveItem(item)}>💼</button>
                        <button className="wl-row-btn"         title="Edit" onClick={() => setEditItem(item)}>✏️</button>
                        <button className="wl-row-btn del"     title="Remove" onClick={() => window.confirm(`Remove ${item.symbol} from watchlist?`) && removeFromWatchlist(item.id)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {showAddModal && (
        <WatchlistModal
          onSave={addToWatchlist}
          onClose={() => setShowAddModal(false)}
        />
      )}
      {editItem && (
        <WatchlistModal
          initial={editItem}
          onSave={(data) => updateWatchlistItem(editItem.id, data)}
          onClose={() => setEditItem(null)}
        />
      )}
      {moveItem && (
        <MoveToPortfolioModal
          item={moveItem}
          onSave={handleMoveToPortfolio}
          onClose={() => setMoveItem(null)}
        />
      )}
    </div>
  );
}